"use client";

/**
 * 欢迎区 + 全局搜索 + 用户信息（2026-10-08）。
 *
 * ## 为什么搜索放在首页而不是顶栏
 * 顶栏已有 7 个图标入口（`GlobalHeader`），再塞一个搜索框会让 h-14 的横条
 * 挤爆（实测 logo + 7 图标 + 语言已接近上限）。而「搜索项目」是**进门第一件事**，
 * 放在欢迎区右侧既符合视觉重心，也不用改顶栏布局。
 *
 * ## 搜索的实现口径
 * 纯前端过滤已加载的最近项目（≤6 条）+ 模块名。**不做全库模糊搜索**：
 * 那需要服务端接口与防抖，是另一个功能；这里只承诺「跳到常用目的地」。
 * 输入框下方给出分组结果（项目 / 功能），↑↓ 可选、Enter 直达。
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { CornerDownLeft, FolderKanban, Search, Sparkles, X } from "lucide-react";

import { F, FOCUS, H, R } from "@/components/studio/ui";
import { HOME_MODULES, resolveModuleHref } from "@/lib/home/modules";
import type { HomePayload } from "@/lib/home/types";

function greetingKey(hour: number): string {
  // 🔴 返回**根路径**（含 home. 前缀），因为用 `tRoot()`（根翻译器）渲染。
  // 若这里返回 "greeting.morning" 而用 useTranslations("home") 的 t() 渲染倒也没错，
  // 但同一个文件里已有大量根路径键（modules.*、search*），两种写法并存必然有人写错。
  // 统一为根路径 + tRoot()，并在门禁里断言（本文件的所有数据层键都不带命名空间前缀）。
  if (hour < 5) return "home.greeting.dawn";
  if (hour < 12) return "home.greeting.morning";
  if (hour < 18) return "home.greeting.afternoon";
  return "home.greeting.evening";
}

export function WelcomeHeader({
  locale,
  basePath,
  userId,
  payload,
}: {
  locale: string;
  basePath: string;
  userId: string;
  payload: HomePayload;
}) {
  const t = useTranslations("home");
  // 🔴 根翻译器（无参）：greetingKey() 返回的是根路径 `home.greeting.*`，
  // 用命名空间翻译器会解析成 `home.home.greeting.*` 并把键名原样印到界面。
  // 实测踩过：tsc 绿、门禁绿、页面 200，只有抓 HTML 才发现问候语是字符串键名。
  const tRoot = useTranslations();
  const router = useRouter();
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // 问候语按本地小时算；只在小时变化时重算（分钟粒度无意义）
  const hour = new Date().getHours();
  const hello = tRoot(greetingKey(hour));

  // "/" 聚焦搜索框：搜索类页面的通用肌肉记忆
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return null;
    // 🔴 href 一律不带 basePath：结果项经 router.push 跳转，框架自动补前缀。
    // 首版手工拼 `${basePath}${p.href}` → 双前缀 404（与模块卡同一根因）。
    const projects = payload.recentProjects
      .filter((p) => p.title.toLowerCase().includes(query))
      .map((p) => ({ kind: "project" as const, id: p.id, label: p.title, href: p.href }));
    // 🔴 labelKey 是根路径 `home.modules.*`，必须用根翻译器 tRoot 解析 ——
    // 用 home 命名空间的 t() 会解析成 home.home.modules.* 并把键名印到结果列表。
    const modules = HOME_MODULES.filter((m) => tRoot(m.labelKey).toLowerCase().includes(query)).map(
      (m) => ({
        kind: "module" as const,
        id: m.id,
        label: tRoot(m.labelKey),
        href: resolveModuleHref(m, locale),
      }),
    );
    return [...projects, ...modules].slice(0, 8);
  }, [q, payload.recentProjects, locale, tRoot]);

  // 结果集变化后把光标收回合法范围：否则删字后 cursor 指向不存在的项，
  // 按 Enter 会跳到 undefined —— 这是搜索框最常见的真实 bug。
  useEffect(() => {
    setCursor(0);
  }, [q]);

  const go = (href: string) => {
    setQ("");
    router.push(href);
  };

  return (
    <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
      <div className="min-w-0">
        <h1 className="font-display text-xl font-bold tracking-tight text-[var(--text-primary)]">
          {hello}
        </h1>
        <p className={`${F.meta} mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5`}>
          <span>{t("subtitle")}</span>
          {payload.authenticated && payload.board.totals.projects > 0 ? (
            <>
              <span aria-hidden className="text-[var(--border-subtle)]">·</span>
              <span className="tabular-nums">
                {t("summary", {
                  projects: payload.board.totals.projects,
                  shots: payload.board.totals.shots,
                })}
              </span>
            </>
          ) : null}
        </p>
      </div>

      <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:w-auto">
        <SearchBox
          ref={inputRef}
          value={q}
          onChange={setQ}
          cursor={cursor}
          onCursor={setCursor}
          results={results}
          onGo={go}
          placeholder={t("searchPlaceholder")}
          emptyText={t("searchEmpty")}
          projectLabel={t("searchGroupProjects")}
          moduleLabel={t("searchGroupModules")}
        />

        {/* 用户信息入口：显示 uid 短码 + 跳转设置。
            🔴 不做成下拉菜单（只有一个动作）——单动作的下拉是纯摩擦。 */}
        <a
          href={`${basePath}/${locale}/settings`}
          title={t("userEntry")}
          className={`${H.sm} ${R.control} flex shrink-0 items-center gap-1.5 border border-[var(--border-subtle)] bg-white px-2.5 ${F.value} transition-colors hover:border-[var(--primary)] hover:text-[var(--primary)] ${FOCUS}`}
        >
          <span
            aria-hidden
            className="flex h-5 w-5 items-center justify-center rounded-md bg-[var(--primary)]/10 text-[10px] font-semibold text-[var(--primary)]"
          >
            U
          </span>
          <span className="font-mono text-xs text-[var(--text-secondary)]">
            {userId ? userId.slice(0, 8) : "—"}
          </span>
        </a>
      </div>
    </header>
  );
}

// ── 搜索框 ──────────────────────────────────────────────────────

interface Result {
  kind: "project" | "module";
  id: string;
  label: string;
  href: string;
}

function SearchBox({
  ref,
  value,
  onChange,
  cursor,
  onCursor,
  results,
  onGo,
  placeholder,
  emptyText,
  projectLabel,
  moduleLabel,
}: {
  ref: React.RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (v: string) => void;
  cursor: number;
  onCursor: (v: number) => void;
  results: Result[] | null;
  onGo: (href: string) => void;
  placeholder: string;
  emptyText: string;
  projectLabel: string;
  moduleLabel: string;
}) {
  return (
    <div className="relative w-full sm:w-[280px] lg:w-[320px]">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-muted)]"
        aria-hidden
      />
      <input
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            onChange("");
            return;
          }
          if (!results || results.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            onCursor((cursor + 1) % results.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            onCursor((cursor - 1 + results.length) % results.length);
          } else if (e.key === "Enter") {
            e.preventDefault();
            // 光标可能因结果集收缩而越界 —— 取模兜底，否则跳到 undefined
            onGo(results[Math.min(cursor, results.length - 1)].href);
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        data-home-search
        className={`${H.sm} w-full ${R.control} border border-[var(--border-subtle)] bg-white pl-8 pr-7 text-[13px] text-[var(--text-primary)] outline-none transition-colors placeholder:text-[var(--text-muted)] focus:border-[var(--primary)]/50 ${FOCUS}`}
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label={placeholder}
          className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-[var(--text-muted)] transition-colors hover:bg-[var(--surface)] hover:text-[var(--text-primary)]"
        >
          <X className="h-3 w-3" />
        </button>
      ) : (
        <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-[var(--border-subtle)] bg-[var(--surface)] px-1 text-[10px] text-[var(--text-muted)] sm:block">
          /
        </kbd>
      )}

      {results ? (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 overflow-hidden rounded-xl border border-[var(--border-subtle)] bg-white shadow-lg shadow-black/5">
          {results.length === 0 ? (
            <p className="px-3 py-3 text-xs text-[var(--text-secondary)]">{emptyText}</p>
          ) : (
            <ul className="max-h-72 overflow-y-auto py-1">
              {/* 分组标题用 i18n，不写死「项目/功能」 */}
              {(["project", "module"] as const).map((kind) => {
                const group = results.filter((r) => r.kind === kind);
                if (group.length === 0) return null;
                return (
                  <li key={kind}>
                    <p className="px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-[var(--text-muted)]">
                      {kind === "project" ? projectLabel : moduleLabel}
                    </p>
                    <ul>
                      {group.map((r) => {
                        const idx = results.indexOf(r);
                        // 🔴 高亮必须读 cursor，不能写死第一项：
                        // 写死会让 ↑↓ 看似可用但视觉零反馈，键盘用户不知道选中的是哪个。
                        const isActive = idx === cursor;
                        return (
                          <li key={`${r.kind}-${r.id}`}>
                            <button
                              type="button"
                              onMouseEnter={() => onCursor(idx)}
                              onClick={() => onGo(r.href)}
                              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
                                isActive
                                  ? "bg-[var(--primary)]/10 text-[var(--primary)]"
                                  : "text-[var(--text-secondary)] hover:bg-[var(--surface)]"
                              }`}
                            >
                              {r.kind === "project" ? (
                                <FolderKanban className="h-3.5 w-3.5 shrink-0" />
                              ) : (
                                <Sparkles className="h-3.5 w-3.5 shrink-0" />
                              )}
                              <span className="min-w-0 flex-1 truncate">{r.label}</span>
                              {isActive ? (
                                <CornerDownLeft className="h-3 w-3 shrink-0 opacity-70" />
                              ) : null}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
